import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  QuestionnaireChatMessage,
  QuestionnaireChatMessageKind,
  QuestionnaireClarification,
  QuestionnaireDraft,
  QuestionnaireDraftNotEditableReason,
  QuestionnaireDraftReadiness,
  QuestionnaireHttpOperation,
  QuestionnaireOperationEffect,
  QuestionnaireOperationsCompileResult,
  QuestionnaireChatTurnReadback,
  QuestionnaireTurnCommand,
  QuestionnaireTurnAttachmentInput,
  QuestionnaireTurnInput,
  QuestionnaireValidateResponse,
  StudyGoals,
  SurveyJson,
  carriedObjectiveFallbackStamp,
  openClarificationOf,
} from "@digital-twins/shared-types";
import { toast } from "sonner";

import { revalidateRail } from "@/lib/chat/revalidateRail";
import {
  applyQuestionnaireOperationsApi,
  validateQuestionnaireOperationsApi,
  compileQuestionnaireDraftApi,
  createQuestionnaireDraftApi,
  getQuestionnaireDraftApi,
  cancelQuestionnaireChatTurnApi,
  getActiveQuestionnaireChatTurnApi,
} from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import {
  enqueueAndAwaitQuestionnaireChatTurn,
  waitForQuestionnaireChatTurn,
} from "@/features/studies/questionnaire/enqueueAndAwaitQuestionnaireChatTurn";
import { newClientTurnId } from "@/features/studies/questionnaire/clientTurnId";
import { readUntilAnswered } from "@/features/studies/questionnaire/readUntilAnswered";
import {
  getCompileErrorMessage,
  isQuestionnaireBundleConflict,
  isQuestionnaireDraftConflict,
} from "@/features/studies/questionnaire/utils/compileErrorMessage";
import { diffDraftToOperations } from "@/features/studies/questionnaire/utils/draftOperations";
import { patchQuestionnaireDraft } from "@/features/studies/questionnaire/utils/patchQuestionnaireDraft";
import {
  buildDraftAudience,
  draftAudienceKey,
  draftNamesNoPopulation,
  type SelectedPopulation,
} from "@/features/studies/questionnaire/utils/draftAudience";
import type { StudyObjectiveUpdate } from "@/features/studies/questionnaire/hooks/useQuestionnaireWorkspace";
import {
  formatTurnFailureCopy,
  requestFailureLine,
  ruleCopy,
  signInActionFor,
  transportFailureCopy,
} from "@/features/studies/questionnaire/utils/questionnaireIssueCopy";
import type { QuestionnaireTurnEndCause } from "@/features/studies/questionnaire/types";
import type { InFlightTurn } from "@/features/studies/questionnaire/components/answeredCards";
import { turnCommandCopy } from "@/features/studies/questionnaire/utils/turnCommandCopy";
import i18n, { chatT, interfaceLanguageCode } from "@/i18n";
import {
  chatLanguage,
  setChatLanguage,
} from "@/features/studies/questionnaire/utils/chatLanguage";

import { AnalyticsEvents, track } from "@/lib/services/analytics";

export const SLOW_WARNING_MS = 180_000;

/**
 * The composer's unsent words, per draft, for this tab. The builder unmounts
 * when the flow goes to Review and on a reload; the words come back with it.
 * Storage that is unavailable keeps them for this mount only.
 */
const UNSENT_COMPOSER_KEY = "questionnaire-composer-unsent:";

function readUnsentComposerText(draftId?: string | null): string {
  if (!draftId) return "";
  try {
    return sessionStorage.getItem(UNSENT_COMPOSER_KEY + draftId) ?? "";
  } catch {
    return "";
  }
}

function writeUnsentComposerText(draftId: string, text: string) {
  try {
    if (text) sessionStorage.setItem(UNSENT_COMPOSER_KEY + draftId, text);
    else sessionStorage.removeItem(UNSENT_COMPOSER_KEY + draftId);
  } catch {
    // Kept for this mount only.
  }
}

interface ChatTranscriptShape {
  messages?: Array<QuestionnaireChatMessage>;
  assistantFollowUpCount?: number;
  assumptions?: Array<{ text?: string } | string>;
  /** The study's agreed objective (Wave 7). Persisted write-once on capture. */
  studyGoals?: StudyGoals;
  /** The questions the builder asked and waited on (plan S4.1). */
  clarifications?: Array<QuestionnaireClarification>;
  /** The language the chat speaks (wave G, D); absent on an older transcript. */
  conversationLanguage?: string;
}

interface UseQuestionnaireDraftSessionOptions {
  sourceThreadToken?: string | null;
  /** §0 goal id — seeds the chat's authored opening chain at creation. */
  goalId?: string | null;
  initialMessage?: string | null;
  language?: string;
  onSessionReady?: (sessionId: string) => void;
  resumeDraftId?: string | null;
  selectedPopulation: SelectedPopulation | null;
}

export interface SavedDraftResult {
  revision?: number;
  draft: QuestionnaireDraft;
  /** The compiled survey of this draft, or null when it did not compile. */
  surveyJson: SurveyJson | null;
  /**
   * The verdict THIS save produced. Returned rather than only stored so the
   * advance path can act on it in the same tick — reading it back off hook
   * state would race the render that publishes it.
   */
  readiness: QuestionnaireDraftReadiness | null;
  /** What the server cascaded to keep every reference resolvable. */
  effects: Array<QuestionnaireOperationEffect>;
  compile?: QuestionnaireOperationsCompileResult;
}

interface UseQuestionnaireDraftSessionResult {
  /**
   * Append a local assistant message (optionally a DecisionCard with options) to
   * the transcript WITHOUT a backend round-trip. Used by the composer asset-add
   * flow to confirm the add + offer the "update the questions?" follow-up.
   * Returns the message's id, which the builder keys the card's own action by.
   */
  appendAssistantPrompt: (args: {
    content: string;
    options?: Array<{ label: string; value: string }>;
  }) => string;
  /**
   * One revision-bound write of typed operations, adopted on success. Intake
   * (collect card, composer asset, video read) sends its asset operations
   * through here; the builder's Save goes through `persistDraftEdits`.
   * `alreadyApplied` is the write's replay rule (see the implementation).
   */
  persistOperations: (
    operations: Array<QuestionnaireHttpOperation>,
    alreadyApplied: (latest: QuestionnaireDraft) => boolean,
    expectedRevision?: number,
  ) => Promise<SavedDraftResult>;
  presentation: "full" | "quick";
  draftRevision?: number;
  assumptions: Array<string>;
  /** Asks the server to stop the current turn (a request, plan S4.4). */
  cancelRequest: () => void;
  /**
   * Whether Stop has a turn to act on: the current turn has named its id and
   * no stop for it is out or accepted.
   */
  canStop: boolean;
  /**
   * The send in flight got no answer yet and is being sent again: the status
   * line says so.
   */
  sendUnconfirmed: boolean;
  compiledSurveyJson: SurveyJson | null;
  draft: QuestionnaireDraft | null;
  draftId: string | null;
  followUpCount: number;
  turnStage: string | null;
  /** Authoring question count from the stage event; null otherwise. */
  turnStageTotal: number | null;
  /** Stop was asked for the turn that is still in flight. */
  stopRequested: boolean;
  input: string;
  isDraftReady: boolean;
  /** True for the duration of a `saveStudyGoals` PATCH. */
  isSavingStudyGoals: boolean;
  messages: Array<QuestionnaireChatMessage>;
  /**
   * Compile the draft as it is CURRENTLY PERSISTED and adopt the result.
   *
   * The advance path's second half, for the case where there is nothing to
   * save. Compilation is deterministic and cheap, so Review always pays for a
   * fresh one rather than reasoning about whether the cached
   * `compiledSurveyJson` still describes the persisted draft — a question the
   * client cannot answer, since the survey does not say which draft produced
   * it. Also the retry seam: a compile that failed is retried by clicking
   * Review again, with no unrelated edit or reload needed.
   *
   * Returns nulls rather than throwing on failure, having already surfaced the
   * backend's reason; a null readiness means "not authorized to advance".
   */
  compilePersistedDraft: () => Promise<{
    surveyJson: SurveyJson | null;
    readiness: QuestionnaireDraftReadiness | null;
    /** The draft revision this survey was compiled at, which launch sends. */
    revision: number | null;
    /** That revision's `draft.audience`: the audience Review shows and launch reads. */
    audience: QuestionnaireDraft["audience"] | null;
  }>;
  /**
   * Save: the edits from `baseDraft` (the accepted draft they started from) to
   * `nextDraft`, as one `POST /operations` at `expectedRevision`. Nothing is
   * sent when they differ only in server-derived fields.
   */
  persistDraftEdits: (
    baseDraft: QuestionnaireDraft,
    nextDraft: QuestionnaireDraft,
    expectedRevision?: number,
  ) => Promise<SavedDraftResult>;
  /**
   * The server's dry run of the Save `persistDraftEdits` would send (the same
   * batch at the same revision, nothing written): the one validity verdict
   * the editor shows and holds the Save on. Null when the edits are only
   * server-derived fields, so there is nothing to send.
   */
  validateDraftEdits: (
    baseDraft: QuestionnaireDraft,
    nextDraft: QuestionnaireDraft,
    expectedRevision?: number,
  ) => Promise<QuestionnaireValidateResponse | null>;
  /**
   * The backend's verdict on the draft currently persisted for this session.
   *
   * Null before the first server draft, and STALE the moment the user edits
   * locally — which is why the advance path saves first and acts on the
   * readiness that save returns. Never re-derive any of its fields here: the
   * question-count floor depends on the ad-instrument roster, which only the
   * backend resolves.
   */
  readiness: QuestionnaireDraftReadiness | null;
  /** Send the failed turn again, after `beforeSend` passes (see the hook). */
  retryLast: (beforeSend?: () => Promise<boolean>) => void;
  canRetryLast: boolean;
  /**
   * Why the loaded draft takes no edits (launched, archived, a copy in
   * progress, an older draft), null when it does (B13). The builder locks on
   * it, and the session says why with the catalog's line.
   */
  notEditableReason: QuestionnaireDraftNotEditableReason | null;
  /**
   * Pre-draft objective write path (Wave 8). The panel's `StudyObjectivesRow`
   * commits a value to its own state first and calls this one render later;
   * this just persists what it is handed, no debouncing of its own. Sets
   * `studyGoals` optimistically, PATCHes `/objective`, and reverts to the
   * prior value on failure. `source` is 'confirmed' only when the caller
   * passes it explicitly (legacy); anything else, including omitted, lands
   * as 'edited', matching the endpoint's narrower contract.
   *
   * Resolves whether the turn committed the objective. False (it failed, was
   * stopped or could not start) reverts the value, and the row reopens its
   * editor on the text.
   */
  saveStudyGoals: (next: StudyObjectiveUpdate) => Promise<boolean>;
  /**
   * One chat turn of the user's words. They answer whatever card is open;
   * their shape never routes them. Resolves whether the turn committed; false
   * when it failed, was stopped or could not start.
   */
  sendMessage: (
    text: string,
    /** The card option the text chose (plan S3.3, L16). */
    optionId?: string,
    /**
     * `composer`: the words are the composer's, so they leave it when the
     * turn starts. Any other turn (a card's option, a command, the page's
     * first message) leaves the composer's unsent words where they are.
     */
    from?: "composer",
    /**
     * The files attached to the words, each stored first (L8): the turn
     * reads them as typed input and its operations place them.
     */
    attachments?: Array<QuestionnaireTurnAttachmentInput>,
  ) => Promise<boolean>;
  /**
   * A typed UI fact as a turn (plan S4.2): a collect receipt, a translation,
   * new assets for the questions, or the objective panel's edit. Resolves
   * whether the turn committed.
   */
  sendCommand: (command: QuestionnaireTurnCommand) => Promise<boolean>;
  /**
   * A turn is in flight: from the send, or from a reload that re-attached to
   * a queued or running turn, until the turn ends. The one fact, read off the
   * session's turn (`inFlightTurn`), never set on its own.
   */
  sending: boolean;
  /**
   * The page is loading its draft. Not a turn: it shows no typing dots and no
   * Stop, and it never ends a turn a reload re-attached to (plan S4.4).
   */
  loadingDraft: boolean;
  sessionError: string | null;
  setInput: (value: string) => void;
  showSlowWarning: boolean;
  startManualDraft: (options?: {
    intent?: "manual" | "paste";
    /** The open card this opens the workspace from (plan S4.1). */
    answers?: { clarificationId: string; optionId?: string };
  }) => Promise<QuestionnaireDraft | undefined>;
  /**
   * The study's agreed objective, straight off `chatState` (never the
   * draft). Null until the marketer answers the turn-zero card.
   *
   * This is the ONLY copy that exists before the first draft builds, so
   * pre-draft it is truth: the turn-zero receipt and the empty builder panel
   * both read it, and `saveStudyGoals` writes it. Once a draft exists the
   * draft's own `studyGoals` outranks it and `StudyObjectivesRow` reads that
   * instead, matching the planner's precedence (`resolveStudyGoals`).
   */
  studyGoals: StudyGoals | null;
  /**
   * The chat transcript's clarification records (plan S4.1): at most one is
   * open, and the next message answers it. The builder reads which card is
   * waiting, and how each card was answered, from these.
   */
  clarifications: Array<QuestionnaireClarification>;
  /**
   * The asset families the server's draft declares (`testedAssets.assetTypes`),
   * read with every draft the session adopts, an intake draft with no
   * workspace yet included (an ad study before its first ad).
   */
  declaredAssetTypes: ReadonlyArray<string>;
  /**
   * The turn in flight: its input as its turn row stores it (the record it
   * names, its command, its option) and its bubble (plan S4.1). From the
   * session's own send, or from the turn's snapshot when a reload
   * re-attaches; null once the turn ends. The builder shows the record it
   * answers as answered while it runs.
   */
  inFlightTurn: InFlightTurn | null;
  /**
   * The answer the next audience write carries: "No, change audience" on the
   * topic gate answers its record in the audience PATCH (plan S4.1).
   */
  answerWithNextAudienceChange: (answers: {
    clarificationId: string;
    optionId?: string;
  }) => void;
}

/**
 * The greeting a draft with no transcript opens with: the builder's words, in
 * the interface language like the rest of its chat. The questionnaire's
 * language is the respondents'.
 */
function buildFallbackQuestion(): QuestionnaireChatMessage {
  return {
    id: "questionnaire-q1",
    role: "assistant",
    content: i18n.t("questionnaire.opening.defaultGreeting", { ns: "app" }),
    kind: "clarifying_question",
    createdAt: new Date().toISOString(),
  };
}

function mapAssumptions(
  rawAssumptions: ChatTranscriptShape["assumptions"],
): Array<string> {
  if (!Array.isArray(rawAssumptions)) {
    return [];
  }

  return rawAssumptions
    .map((assumption) =>
      typeof assumption === "string"
        ? assumption
        : String(assumption?.text || ""),
    )
    .filter(Boolean);
}

function buildTurnFailureAssistantMessage(content: string): QuestionnaireChatMessage {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `turn_failure_${crypto.randomUUID()}`
      : `turn_failure_${Date.now()}`;
  return {
    id,
    role: "assistant",
    language: chatLanguage(),
    content,
    kind: "error",
    createdAt: new Date().toISOString(),
  };
}

let localMessageSequence = 0;

/**
 * The id of a message the builder writes itself and the server never has
 * (`local_`): its notes and cards, and a turn's bubbles. Adopting the
 * server's transcript keeps every one in its place (`withLocalNotes`).
 */
function localMessageId(): string {
  localMessageSequence += 1;
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? `local_${crypto.randomUUID()}`
    : `local_${Date.now()}_${localMessageSequence}`;
}

/** A turn's failure line, as a message of the transcript it stays in. */
function turnFailureNote(content: string): QuestionnaireChatMessage {
  return { ...buildTurnFailureAssistantMessage(content), id: localMessageId() };
}

/**
 * The Retry of a turn that stopped after its commit landed (#3): what the
 * commit left undone, as the `resume` command. Its words are saved and its
 * record answered, so they are never sent again.
 */
const RESUME_TURN_INPUT: QuestionnaireTurnInput = {
  message: null,
  command: { kind: "resume" },
  optionId: null,
  answersClarificationId: null,
};

/** The waiter's answer for a turn that ended `cancelled` (plan S4.4). */
function isTurnCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * Enqueue refused: the record the turn names was answered or replaced
 * elsewhere, for example in a second tab (plan S4.1).
 */
const CLARIFICATION_NOT_OPEN = "clarification_not_open";
/**
 * Refusals that say the draft moved under the turn before it was queued:
 * the record it answers was answered elsewhere, or the draft is past the
 * revision it was sent against (S2c). The builder reads the draft as it is
 * now, so the next send carries the current state.
 */
const DRAFT_MOVED_TURN_REFUSALS: ReadonlySet<string> = new Set([
  CLARIFICATION_NOT_OPEN,
  "questionnaire_edit_revision_conflict",
]);

/**
 * The enqueue's refusal while the draft already runs a turn: one a Stop
 * before its id let land, or another tab's. The page watches it on the
 * reload's road (`attachLatestTurn`).
 */
const TURN_IN_FLIGHT = "questionnaire_turn_in_flight";

/**
 * The turn the builder shows (plan S4.1–S4.4): the one in flight, or the last
 * one once it failed. One slot and one owner, for the live send and a reload
 * alike. Its input is what its turn row stores. A running turn's bubble is
 * drawn from it; once the turn ends short, its bubble and failure line join
 * the builder's own notes in the transcript, in order (`keepEndedTurn`), and
 * stay there when a later turn runs.
 */
interface SessionTurn {
  /** What the turn asks, as its row stores it; a Retry resends exactly this. */
  input: QuestionnaireTurnInput;
  /**
   * The user's send this turn is (its enqueue's idempotency id), minted once
   * per send. Null for a turn this page did not send (a reload found it).
   */
  clientTurnId: string | null;
  /**
   * The user's bubble: shown while the turn runs and after it fails, so the
   * user sees what was sent beside why it failed. Null once the transcript
   * carries the turn's own message.
   */
  bubble: QuestionnaireChatMessage | null;
  /**
   * The draft revision the turn was sent against (its row's
   * `draft_revision_before`). A failed turn committed nothing, so its failure
   * is shown only while the draft is still at this revision.
   */
  revision: number | null;
  /**
   * When the server started a turn this page found running (its row's
   * `createdAt`), else null. The draft read on a reload can already hold what
   * that turn committed: its message, and the fixed line its written reply
   * replaces (AD-19). A turn this page sent adopts its transcript only when it
   * ends, so it has none.
   */
  startedAt: string | null;
  /**
   * Its send got no answer (offline, a dropped connection, a timeout) and is
   * being sent again with the same `clientTurnId`, until one arrives.
   */
  sendUnconfirmed: boolean;
  /** How the turn failed; null while it runs. */
  failure: {
    title: string;
    description: string | null;
    /** The failing owner's own verdict: Retry is offered only when true. */
    retryable: boolean;
    /**
     * Nobody said whether the send was queued (its enqueue got no answer):
     * Retry sends the same turn (`clientTurnId`), so a send that did land is
     * never queued twice. Any other failure's Retry is a new send.
     */
    sameTurn: boolean;
    bubble: QuestionnaireChatMessage;
  } | null;
}

/**
 * A turn's persisted cause in the `response.data` shape a failed request has,
 * which `formatTurnFailureCopy` reads.
 */
function turnEndError(cause: QuestionnaireTurnEndCause) {
  return {
    response: {
      data: {
        code: cause.errorCode,
        message: cause.errorMessage,
        failureCodes: cause.failureCodes,
        failureDetails: cause.failureDetails,
        retryable: cause.retryable,
      },
    },
  };
}

/**
 * A turn that stopped after one of its commits landed (#3). The transcript
 * carries its message, so it has no bubble. Its notice is its cause, said
 * after what was saved, through the one turn-end renderer, and is shown,
 * like a failure, while the draft is still at the revision that commit
 * made. Its cause's own verdict offers the Retry, as for any failure on that
 * cause; the Retry resumes what the commit left undone.
 */
function stoppedAfterSaveTurn(
  revision: number | null,
  cause: QuestionnaireTurnEndCause,
  draftId: string,
): SessionTurn {
  const copy = formatTurnFailureCopy(turnEndError(cause), draftId, {
    committed: true,
  });
  return {
    input: RESUME_TURN_INPUT,
    clientTurnId: null,
    bubble: null,
    revision,
    startedAt: null,
    sendUnconfirmed: false,
    failure: {
      title: copy.title,
      description: copy.description,
      retryable: copy.retryable,
      sameTurn: false,
      bubble: turnFailureNote(copy.title),
    },
  };
}

/**
 * The catalog's line for a draft that takes no edits: an older draft's own
 * refusal, else the lifecycle one.
 */
function notEditableLine(reason: QuestionnaireDraftNotEditableReason): string {
  const code =
    reason === "legacy_read_only"
      ? "questionnaire_draft_read_only"
      : reason === "archived"
        ? "questionnaire_draft_archived"
        : "questionnaire_draft_not_editable";
  return ruleCopy(code, undefined, chatLanguage()) ?? "";
}

/** A turn is sent against the draft revision the page holds (S2c). */
function revisionToSend(revision: number | null): number {
  if (revision === null) throw new Error("Reload the draft before sending.");
  return revision;
}

function turnBubble(
  input: QuestionnaireTurnInput,
  id: string,
  createdAt: string,
  kind: QuestionnaireChatMessageKind,
): QuestionnaireChatMessage {
  return {
    id,
    role: "user",
    content: input.command
      ? turnCommandCopy(input.command, chatLanguage())
      : (input.message ?? ""),
    kind,
    createdAt,
    ...(input.command ? { command: input.command } : {}),
  };
}

/**
 * The words a turn that did not commit hands back to the composer: the
 * user's own typed words. A card's answer goes back to its card, which asks
 * again, and a command is not something the user typed.
 */
function composerTextOf(
  input: QuestionnaireTurnInput,
  clarifications: ReadonlyArray<QuestionnaireClarification>,
  messages: ReadonlyArray<QuestionnaireChatMessage>,
): string | null {
  if (!input.message || input.optionId) return null;
  const record = clarifications.find(
    (entry) => entry.id === input.answersClarificationId,
  );
  const card = record
    ? messages.find((message) => message.id === record.messageId)
    : undefined;
  if (card?.options?.length || card?.collect) return null;
  return input.message;
}

function appendTurnFailureBubble(
  setMessages: (
    updater: (current: Array<QuestionnaireChatMessage>) => Array<QuestionnaireChatMessage>,
  ) => void,
  content: string,
): void {
  setMessages((current) => {
    const last = current[current.length - 1];
    if (last?.role === "assistant" && last.content === content) {
      return current;
    }
    return [...current, buildTurnFailureAssistantMessage(content)];
  });
}

/**
 * Adopting the server's transcript keeps the builder's own notes and cards
 * (`local_`: `appendAssistantPrompt`, and a turn that ended short), which the
 * server never has: each stays after the transcript message it followed. A
 * turn's transcript used to replace them, so the cards that started the turn
 * and the notes written before it vanished when it landed.
 */
function withLocalNotes(
  previous: ReadonlyArray<QuestionnaireChatMessage>,
  transcript: Array<QuestionnaireChatMessage>,
): Array<QuestionnaireChatMessage> {
  const isLocalNote = (message: QuestionnaireChatMessage) =>
    message.id.startsWith("local_");
  if (!previous.some(isLocalNote)) return transcript;
  const inTranscript = new Set(transcript.map((message) => message.id));
  const notesAfter = new Map<string | null, Array<QuestionnaireChatMessage>>();
  let anchor: string | null = null;
  for (const message of previous) {
    if (isLocalNote(message)) {
      notesAfter.set(anchor, [...(notesAfter.get(anchor) ?? []), message]);
    } else if (inTranscript.has(message.id)) {
      anchor = message.id;
    }
  }
  return [
    ...(notesAfter.get(null) ?? []),
    ...transcript.flatMap((message) => [
      message,
      ...(notesAfter.get(message.id) ?? []),
    ]),
  ];
}

/** A command turn's bubble in the builder's own words (plan S4.2). */
function localizeMessages(
  messages: Array<QuestionnaireChatMessage>,
): Array<QuestionnaireChatMessage> {
  return messages.map((msg) =>
    msg.command
      ? { ...msg, content: turnCommandCopy(msg.command, msg.language) }
      : msg,
  );
}

function parseChatTranscript(value: unknown) {
  const transcript = (value || {}) as ChatTranscriptShape;
  const messages = Array.isArray(transcript.messages)
    ? transcript.messages
    : [];

  return {
    assumptions: mapAssumptions(transcript.assumptions),
    followUpCount:
      typeof transcript.assistantFollowUpCount === "number"
        ? transcript.assistantFollowUpCount
        : 0,
    messages:
      messages.length > 0 ? messages : [buildFallbackQuestion()],
    conversationLanguage: transcript.conversationLanguage,
    studyGoals: transcript.studyGoals ?? null,
    clarifications: Array.isArray(transcript.clarifications)
      ? transcript.clarifications
      : [],
  };
}

/**
 * Workspace-hydration gate: a draft worth rendering in the builder workspace.
 *
 * Research questions still open the full questionnaire panel. Text-collect
 * opening turns (claims / pains / pricing / concept) also persist tested
 * assets before any questions exist: those assets-only shells must mount so
 * ConceptAssetsPanel can confirm the paste split before PLAN.
 *
 * Review and compile still ask `readiness` from the backend; this predicate is
 * only "is there anything to render".
 */
function hasWorkspaceDraft(value: unknown): value is QuestionnaireDraft {
  const draft = value as QuestionnaireDraft | null;
  if (
    !draft ||
    typeof draft !== "object" ||
    !Array.isArray(draft.researchQuestions)
  ) {
    return false;
  }
  if (draft.researchQuestions.length >= 1) {
    return true;
  }
  // "Build manually" now opens an empty workspace (Add question only).
  // Status draft_ready is the signal that the marketer chose that path;
  // a fresh pre-generation draft stays on the empty-state card.
  if (draft.status === "draft_ready") {
    return true;
  }
  const assets = draft.testedAssets?.assets;
  return Array.isArray(assets) && assets.length > 0;
}

/** The families a tested-asset block declares, or none. */
function declaredAssetTypesOf(block: unknown): Array<string> {
  const types = (block as { assetTypes?: unknown } | null | undefined)
    ?.assetTypes;
  return Array.isArray(types)
    ? types.filter((type): type is string => typeof type === "string")
    : [];
}

function syncDraftAssetsFromResponse(
  response: Awaited<ReturnType<typeof getQuestionnaireDraftApi>>,
  options?: { studyGoals?: StudyGoals | null },
) {
  const draft = response.artifacts.draft as
    | QuestionnaireDraft
    | null
    | undefined;
  const testedAssets =
    response.artifacts.testedAssets ?? draft?.testedAssets ?? null;
  const assetCount = Array.isArray(testedAssets?.assets)
    ? testedAssets.assets.length
    : 0;
  // Prefer the draft artifact when it is already adoptable; otherwise build an
  // assets-only shell from the testedAssets lane so a text-collect persist
  // still surfaces cards when the draft JSON is still empty/placeholder.
  const base =
    draft &&
    hasWorkspaceDraft({
      ...draft,
      testedAssets: testedAssets ?? draft.testedAssets,
    })
      ? draft
      : assetCount > 0 && draft
        ? { ...draft, testedAssets: testedAssets! }
        : assetCount > 0
          ? // An assets-only shell, not a complete draft: the workspace reads
            // its tested assets until the draft artifact lands.
            ({
              schemaVersion: "questionnaire-draft.v1",
              id: response.id,
              researchQuestions: [],
              testedAssets: testedAssets!,
              assumptions: [],
            } as unknown as QuestionnaireDraft)
          : null;
  if (!base) {
    return null;
  }
  return {
    ...base,
    testedAssets: testedAssets ?? base.testedAssets,
    // Chat-state objective can land before the draft artifact carries it;
    // keep the band visible when we promote an assets-only shell.
    studyGoals: base.studyGoals ?? options?.studyGoals ?? undefined,
  };
}

export function useQuestionnaireDraftSession(
  options: UseQuestionnaireDraftSessionOptions,
): UseQuestionnaireDraftSessionResult {
  const {
    goalId,
    sourceThreadToken,
    initialMessage,
    language,
    onSessionReady,
    resumeDraftId,
    selectedPopulation,
  } = options;

  const [compiledSurveyJson, setCompiledSurveyJson] =
    useState<SurveyJson | null>(null);
  const [draft, setDraft] = useState<QuestionnaireDraft | null>(null);
  // The draft a committed turn replaces, which numbers a question it deleted
  // in the turn's effect lines.
  const [readiness, setReadiness] =
    useState<QuestionnaireDraftReadiness | null>(null);
  // Audience currently stored on the SERVER: its population and which
  // population, filter and segments it names (`draftAudienceKey`). Tracked
  // separately from `draft` because the audience lives on the draft artifact
  // even before any questionnaire exists — reading it off `draft` misses the
  // whole pre-generation window, which is exactly when the topic gate runs.
  const [persistedAudienceId, setPersistedAudienceId] = useState<string | null>(
    null,
  );
  const [persistedAudienceKey, setPersistedAudienceKey] = useState<
    string | null
  >(null);
  const [draftId, setDraftId] = useState<string | null>(resumeDraftId || null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [input, setInput] = useState(() =>
    readUnsentComposerText(resumeDraftId),
  );
  useEffect(() => {
    if (draftId) writeUnsentComposerText(draftId, input);
  }, [draftId, input]);
  // The accepted transcript (the server's), plus the builder's own local
  // notes. The turn's bubbles are never written here: they are drawn from the
  // session's turn (below).
  const [messages, setMessages] = useState<Array<QuestionnaireChatMessage>>([]);
  const messagesRef = useRef<Array<QuestionnaireChatMessage>>([]);
  messagesRef.current = messages;
  // The turn the builder shows, in flight or failed (see `SessionTurn`). The
  // ref is read at send time, so two quick sends never start two turns.
  const [turn, setTurnState] = useState<SessionTurn | null>(null);
  const turnRef = useRef<SessionTurn | null>(null);
  // The send in flight's own abort: Stop before the server names the turn.
  const sendAbortRef = useRef<AbortController | null>(null);
  // The reload's road to the draft's latest turn (`attachLatestTurn`), which
  // a send refused with `questionnaire_turn_in_flight` takes too.
  const attachLatestTurnRef = useRef<
    (targetDraftId: string, signal: AbortSignal) => Promise<void>
  >(async () => {});
  // The page's own signal: a turn sent here stops being watched, and its end
  // writes and compiles nothing, once the page unmounts. The abort waits a
  // tick, so a remount in the same tick (React's StrictMode check) keeps it.
  const [pageAbort] = useState(() => new AbortController());
  const pageUnmountRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(pageUnmountRef.current);
    return () => {
      pageUnmountRef.current = setTimeout(() => pageAbort.abort(), 0);
    };
  }, [pageAbort]);
  const setTurn = useCallback((next: SessionTurn | null) => {
    turnRef.current = next;
    setTurnState(next);
  }, []);
  // A turn is in flight: the one fact, off the turn itself (plan S4.4).
  const sending = turn !== null && turn.failure === null;
  // The page's own draft load. Kept apart from the turn: it shows no typing
  // dots and no Stop, and its end never unlocks a turn a reload re-attached
  // to. A failed turn read back on load waits for it, to compare revisions.
  const [loadingDraft, setLoadingDraft] = useState(true);
  const draftLoadedRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
  } | null>(null);
  if (!draftLoadedRef.current) {
    let resolve!: () => void;
    const promise = new Promise<void>((done) => {
      resolve = done;
    });
    draftLoadedRef.current = { promise, resolve };
  }
  const markDraftLoaded = useCallback(() => {
    setLoadingDraft(false);
    draftLoadedRef.current?.resolve();
  }, []);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [notEditableReason, setNotEditableReason] =
    useState<QuestionnaireDraftNotEditableReason | null>(null);
  const notEditableReasonRef = useRef<QuestionnaireDraftNotEditableReason | null>(
    null,
  );
  const [showSlowWarning, setShowSlowWarning] = useState(false);
  const [assumptions, setAssumptions] = useState<Array<string>>([]);
  // The study's agreed objective, off `chatState` only (never the draft):
  // see the `studyGoals` field doc on UseQuestionnaireDraftSessionResult.
  const [studyGoals, setStudyGoals] = useState<StudyGoals | null>(null);
  const [clarifications, setClarifications] = useState<
    Array<QuestionnaireClarification>
  >([]);
  const [declaredAssetTypes, setDeclaredAssetTypes] = useState<
    ReadonlyArray<string>
  >([]);
  // Read at send time, never through a stale closure.
  const clarificationsRef = useRef<Array<QuestionnaireClarification>>([]);
  clarificationsRef.current = clarifications;
  // The objective panel's save and the page load's first message are turns;
  // they are defined before the turn sender, so they reach it through this
  // ref.
  const sendTurnRef = useRef<
    | ((input: {
        text?: string;
        command?: QuestionnaireTurnCommand;
        draftId?: string;
      }) => Promise<boolean>)
    | null
  >(null);
  const audienceAnswerRef = useRef<{
    clarificationId: string;
    optionId?: string;
  } | null>(null);
  const [isSavingStudyGoals, setIsSavingStudyGoals] = useState(false);
  const [turnStage, setTurnStage] = useState<string | null>(null);
  // The authoring question count the stage event carries, for the progress line.
  const [turnStageTotal, setTurnStageTotal] = useState<number | null>(null);
  const assignTurnStage = useCallback(
    (stage: string | null, total?: number | null) => {
      setTurnStage(stage);
      setTurnStageTotal(typeof total === "number" && total > 0 ? total : null);
    },
    [],
  );
  // The turn Stop acts on: the current turn's id, from when the server names
  // it until the turn ends (`sending` goes false). Never a previous turn's.
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  // The turn whose stop was asked for. One request per turn: Stop stays off
  // for it while the request is out and once it is accepted.
  const [stopRequestedTurnId, setStopRequestedTurnId] = useState<
    string | null
  >(null);
  const bootstrapRef = useRef(false);
  // The page's start (create or load its draft, then its first message),
  // kept so a failed start can run again: from Retry, or from a send made
  // while there is no draft yet (B17). Resolves the draft's id, or null when
  // the start failed.
  const bootstrapRunRef = useRef<(() => Promise<string | null>) | null>(null);
  // A start that failed, and whether its Retry is offered: only when nobody
  // can tell whether it landed (a transport failure, `transportFailureCopy`).
  const [bootstrapFailure, setBootstrapFailureState] = useState<{
    retryable: boolean;
  } | null>(null);
  const bootstrapFailureRef = useRef<{ retryable: boolean } | null>(null);
  const setBootstrapFailure = useCallback(
    (next: { retryable: boolean } | null) => {
      bootstrapFailureRef.current = next;
      setBootstrapFailureState(next);
    },
    [],
  );
  // Counts the turns this session starts. A reload's readback of the latest
  // turn is dropped when the session started one of its own meanwhile.
  const turnSequenceRef = useRef(0);
  // ETag-shaped revision from the last hydrate/persist. Sent as
  // `expectedRevision` so a two-tab write refuses instead of last-writer-wins.
  const draftRevisionRef = useRef<number | undefined>(undefined);
  const [draftRevision, setDraftRevision] = useState<number | undefined>();
  const [presentation, setPresentation] = useState<"full" | "quick">("full");
  function adoptDraftRevision(revision?: number) {
    if (typeof revision !== "number") return true;
    // Never walk backwards: compile can land after a GET snapshot was taken
    // (resume hydrates the pre-compile row), and a later persist must not
    // send that stale number as expectedRevision.
    if (
      draftRevisionRef.current !== undefined &&
      revision < draftRevisionRef.current
    ) {
      return false;
    }
    draftRevisionRef.current = revision;
    setDraftRevision(revision);
    return true;
  }
  const compileInflightRef = useRef<
    Map<
      string,
      Promise<Awaited<ReturnType<typeof compileQuestionnaireDraftApi>>>
    >
  >(new Map());

  const compileDraftSingleFlight = useCallback(
    async (targetDraftId: string) => {
      const existing = compileInflightRef.current.get(targetDraftId);
      if (existing) {
        return existing;
      }

      const flight = (async () => {
        try {
          const compiled = await compileQuestionnaireDraftApi(targetDraftId);
          if (compiled.draft) {
            if (!adoptDraftRevision(compiled.revision))
              throw new Error(
                "A newer questionnaire was accepted while the preview was compiling.",
              );
            setDraft(compiled.draft);
          }
          return compiled;
        } catch (error) {
          if (!isQuestionnaireBundleConflict(error)) {
            throw error;
          }
          const compiled = await compileQuestionnaireDraftApi(targetDraftId);
          if (compiled.draft) {
            if (!adoptDraftRevision(compiled.revision))
              throw new Error(
                "A newer questionnaire was accepted while the preview was compiling.",
              );
            setDraft(compiled.draft);
          }
          return compiled;
        }
      })();

      compileInflightRef.current.set(targetDraftId, flight);
      return flight.finally(() => {
        if (compileInflightRef.current.get(targetDraftId) === flight) {
          compileInflightRef.current.delete(targetDraftId);
        }
      });
    },
    [],
  );

  useEffect(() => {
    if (!sending) {
      setShowSlowWarning(false);
      // The turn is over: Stop has nothing to act on until the next turn
      // names its id.
      setActiveTurnId(null);
      return;
    }

    setShowSlowWarning(false);
    const timer = window.setTimeout(
      () => setShowSlowWarning(true),
      SLOW_WARNING_MS,
    );
    return () => window.clearTimeout(timer);
  }, [sending]);

  const hydrateFromDraftResponse = useCallback(
    (response: Awaited<ReturnType<typeof getQuestionnaireDraftApi>>) => {
      const transcript = parseChatTranscript(response.artifacts.chatTranscript);
      setChatLanguage(transcript.conversationLanguage);
      const generatedDraft = syncDraftAssetsFromResponse(response, {
        studyGoals: transcript.studyGoals,
      });
      if (!adoptDraftRevision(response.revision)) return false;
      // A draft that takes no edits is locked from its load, with the line
      // its refused writes would say (B13).
      const readOnly = response.notEditableReason ?? null;
      notEditableReasonRef.current = readOnly;
      setNotEditableReason(readOnly);
      if (readOnly) {
        setTurn(null);
      }
      setDraft(generatedDraft);
      setDeclaredAssetTypes(
        declaredAssetTypesOf(
          response.artifacts.testedAssets ??
            (response.artifacts.draft as QuestionnaireDraft | null | undefined)
              ?.testedAssets,
        ),
      );
      // Adopted with the draft, never independently: the two are one unit of
      // truth, and a verdict that outlives the draft it describes would be read
      // as a verdict about bytes it has never seen.
      setReadiness(response.readiness ?? null);
      setDraftId(response.id);
      setPresentation(response.presentation ?? "full");
      const storedAudience = (response.artifacts.draft as any)?.audience;
      setPersistedAudienceId(
        (storedAudience?.populationId as string | undefined) ?? null,
      );
      setPersistedAudienceKey(draftAudienceKey(storedAudience));
      setMessages((previous) =>
        withLocalNotes(
          previous,
          localizeMessages(transcript.messages),
        ),
      );
      setFollowUpCount(transcript.followUpCount);
      setStudyGoals(transcript.studyGoals);
      setClarifications(transcript.clarifications);
      setAssumptions(
        transcript.assumptions.length > 0
          ? transcript.assumptions
          : (generatedDraft?.assumptions || [])
              .map((assumption) => assumption.text)
              .filter(Boolean),
      );
      // Hydrated for PREVIEW only. A persisted survey cannot say which draft
      // produced it, so it is never what Review advances with: a draft saved
      // after its last successful compile comes back as survey(D1) beside
      // readiness(D2), and `compilePersistedDraft` is what resolves that.
      setCompiledSurveyJson(
        generatedDraft
          ? (response.artifacts.compiledSurveyJson as SurveyJson | null) || null
          : null,
      );
      setSessionError(readOnly ? notEditableLine(readOnly) : null);
      onSessionReady?.(response.id);
      return true;
    },
    [onSessionReady],
  );

  /**
   * After a conflicting write, adopt the draft the server holds now: its
   * revision, questionnaire, readiness and preview, and nothing else. The
   * transcript, an in-flight chat message and the session error are left as
   * they are. The builder keeps its unsaved edits and sends them at this
   * draft's revision, so the next Save applies them to it instead of 409ing
   * again.
   */
  const adoptAcceptedDraft = useCallback(
    (response: Awaited<ReturnType<typeof getQuestionnaireDraftApi>>) => {
      if (!adoptDraftRevision(response.revision)) return null;
      const accepted = syncDraftAssetsFromResponse(response);
      setDraft(accepted);
      setReadiness(response.readiness ?? null);
      setCompiledSurveyJson(
        (response.artifacts.compiledSurveyJson as SurveyJson | null) ?? null,
      );
      return accepted;
    },
    [],
  );

  const persistDraftPatch = useCallback(
    async (
      targetDraftId: string,
      payload: Record<string, unknown>,
      expectedRevision?: number,
    ) => {
      const result = await patchQuestionnaireDraft(
        targetDraftId,
        payload,
        expectedRevision ?? draftRevisionRef.current,
      );
      if (result.kind === "conflict") {
        if (result.latest) adoptAcceptedDraft(result.latest);
        throw result.error;
      }
      const updated = result.draft;
      if (!adoptDraftRevision(updated.revision))
        throw new Error(
          "A newer questionnaire was accepted while saving. Your local edits are still available.",
        );
      return updated;
    },
    [adoptAcceptedDraft],
  );

  /**
   * The operation response carries the transcript the server just stored.
   * Panel saves used to adopt the draft and leave the sidebar on the previous
   * summary until the next full reload.
   */
  const adoptTranscriptMessages = useCallback((chatTranscript: unknown) => {
    const raw = (chatTranscript || {}) as { messages?: unknown };
    if (!Array.isArray(raw.messages) || raw.messages.length === 0) return;
    const transcript = parseChatTranscript(chatTranscript);
    setChatLanguage(transcript.conversationLanguage);
    setMessages((previous) =>
      withLocalNotes(previous, localizeMessages(transcript.messages)),
    );
  }, []);

  // Another tab's Save or chat turn moves the draft, and this tab used to
  // learn of it only from its own next Save's 409 (E-session-06). When the
  // tab comes back into view, it reads the draft once and adopts a newer
  // revision the way a 409 does (`adoptAcceptedDraft`, with its transcript):
  // the builder keeps its unsaved edits, says the draft was updated, and its
  // next Save applies them to it. Not while a turn of this tab runs: its end
  // adopts its draft.
  useEffect(() => {
    if (!draftId) return;
    let reading = false;
    const readLatest = () => {
      if (document.visibilityState !== "visible" || reading) return;
      const current = turnRef.current;
      if (current !== null && current.failure === null) return;
      reading = true;
      void getQuestionnaireDraftApi(draftId)
        .then((latest) => {
          const known = draftRevisionRef.current;
          if (
            typeof latest.revision !== "number" ||
            (known !== undefined && latest.revision <= known)
          )
            return;
          if (adoptAcceptedDraft(latest))
            adoptTranscriptMessages(latest.artifacts.chatTranscript);
        })
        .catch((error) =>
          console.warn("Failed to read the draft on return:", error),
        )
        .finally(() => {
          reading = false;
        });
    };
    document.addEventListener("visibilitychange", readLatest);
    window.addEventListener("focus", readLatest);
    return () => {
      document.removeEventListener("visibilitychange", readLatest);
      window.removeEventListener("focus", readLatest);
    };
  }, [adoptAcceptedDraft, adoptTranscriptMessages, draftId]);

  /**
   * One revision-bound write of typed operations. The server applies them
   * atomically, compiles in the same commit and answers with the accepted
   * draft, which is adopted here. A refusal (400/422) writes nothing and
   * rethrows. A 409 adopts the draft the server holds now (the builder
   * keeps its unsaved edits for its next Save) and rethrows, unless
   * `alreadyApplied` says that draft already carries this batch: a replay
   * whose first response was lost, which is this write's result. Every
   * writer states that rule; it is not optional.
   */
  const persistOperations = useCallback(
    async (
      operations: Array<QuestionnaireHttpOperation>,
      alreadyApplied: (latest: QuestionnaireDraft) => boolean,
      expectedRevision?: number,
    ): Promise<SavedDraftResult> => {
      if (!draftId) throw new Error("Draft session is not ready");
      const basis = expectedRevision ?? draftRevisionRef.current;
      if (basis === undefined)
        throw new Error("Reload the draft before saving changes.");
      let response: Awaited<ReturnType<typeof applyQuestionnaireOperationsApi>>;
      try {
        response = await applyQuestionnaireOperationsApi(draftId, {
          expectedRevision: basis,
          operations,
        });
      } catch (error) {
        if (!isQuestionnaireDraftConflict(error)) throw error;
        const latest = await getQuestionnaireDraftApi(draftId).catch(
          () => undefined,
        );
        const latestDraft = latest ? adoptAcceptedDraft(latest) : null;
        if (!latest || !latestDraft || !alreadyApplied(latestDraft))
          throw error;
        adoptTranscriptMessages(latest.artifacts.chatTranscript);
        return {
          draft: latestDraft,
          surveyJson:
            (latest.artifacts.compiledSurveyJson as SurveyJson | null) ?? null,
          readiness: latest.readiness ?? null,
          revision: latest.revision,
          effects: [],
        };
      }
      const accepted =
        syncDraftAssetsFromResponse(response) ??
        (response.artifacts.draft as QuestionnaireDraft);
      const readiness = response.readiness ?? null;
      // The survey stored with this draft is its own compile only when the
      // commit compiled it; otherwise there is no preview for this draft.
      const surveyJson =
        response.compile?.status === "compiled"
          ? ((response.artifacts.compiledSurveyJson as SurveyJson | null) ??
            null)
          : null;
      // A response that lands behind a newer accepted draft (one that already
      // contains this write) is this write's result, but the session keeps
      // the newer draft; the builder adopts it next.
      if (adoptDraftRevision(response.revision)) {
        setDraft(accepted);
        setReadiness(readiness);
        setCompiledSurveyJson(surveyJson);
        adoptTranscriptMessages(response.artifacts.chatTranscript);
      }
      return {
        draft: accepted,
        surveyJson,
        readiness,
        revision: response.revision,
        effects: Array.isArray(response.effects) ? response.effects : [],
        compile: response.compile,
      };
    },
    [adoptAcceptedDraft, adoptTranscriptMessages, draftId],
  );

  // Write an audience CHANGE through to the draft.
  //
  // The wizard keeps the chosen audience in client state (SELECT_POPULATION,
  // SET_POPULATION_FILTER) until the draft holds it. Swapping audience, or its
  // filter or segments, mid-session therefore updated the builder header while
  // the persisted draft kept the original — and since the planner request and
  // the launch read the persisted draft, the study ran on the audience the
  // user just replaced.
  //
  // Bootstrap covers the placeholder case (a duplicate whose audience is
  // chosen afterwards); this covers a genuine swap, filter changes included.
  // It is the same write as any audience change, so Review compiles again.
  const attemptedAudienceKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draftId || !selectedPopulation) {
      return;
    }
    // Nothing stored yet, or still the placeholder: bootstrap's job.
    if (draftNamesNoPopulation({ populationId: persistedAudienceId })) {
      return;
    }
    const audience = buildDraftAudience(selectedPopulation);
    const targetKey = draftAudienceKey(audience);
    if (persistedAudienceKey === targetKey) {
      return;
    }
    // Write each target at most once. Without this the hydrate below re-seeds
    // the persisted audience from the server response, and if that response
    // has not caught up the effect fires again in a loop.
    if (attemptedAudienceKeyRef.current === targetKey) {
      return;
    }
    attemptedAudienceKeyRef.current = targetKey;

    void (async () => {
      // "No, change audience" answers the topic gate in this same write,
      // while its record is still the open one.
      const pendingAnswer = audienceAnswerRef.current;
      const answers =
        pendingAnswer &&
        openClarificationOf({ clarifications: clarificationsRef.current })
          ?.id === pendingAnswer.clarificationId
          ? pendingAnswer
          : null;
      const payload = {
        audience,
        ...(answers ? { answers } : {}),
      };
      try {
        let updated;
        try {
          updated = await persistDraftPatch(draftId, payload);
        } catch (error) {
          // Lost to a newer draft: persistDraftPatch has adopted it, so one
          // retry on its revision is the user's audience choice, not a loop.
          if (!isQuestionnaireDraftConflict(error)) throw error;
          updated = await persistDraftPatch(draftId, payload);
        }
        audienceAnswerRef.current = null;
        hydrateFromDraftResponse(updated);
      } catch (error) {
        // A transient failure clears the claim so a later render retries
        // rather than stranding the draft on the audience the user replaced.
        // A second conflict keeps it: retrying would only race again.
        if (!isQuestionnaireDraftConflict(error))
          attemptedAudienceKeyRef.current = null;
        console.error("Failed to persist the audience change:", error);
        toast.error(
          getCompileErrorMessage(error, "Couldn't save the audience change."),
        );
      }
    })();
  }, [
    draftId,
    persistedAudienceId,
    persistedAudienceKey,
    selectedPopulation,
    hydrateFromDraftResponse,
    persistDraftPatch,
  ]);

  /**
   * A turn that ended short: the slot keeps it for its Retry, and its bubble
   * and failure line join the transcript the moment it ends, as the builder's
   * own notes. They stay in order: a note written after it (the material
   * card's line) comes after it, and a later turn never takes them away
   * (NA-09). Only its own Retry takes them back (`retryLast`).
   */
  const keepEndedTurn = useCallback(
    (ended: SessionTurn) => {
      setTurn(ended);
      const notes = [ended.bubble, ended.failure?.bubble].filter(
        (note): note is QuestionnaireChatMessage => Boolean(note),
      );
      setMessages((current) => [
        ...current,
        ...notes.filter((note) => !current.some(({ id }) => id === note.id)),
      ]);
    },
    [setTurn],
  );

  /** A turn's typed words go back to the composer, unless it holds new ones. */
  const handBackWords = useCallback((input: QuestionnaireTurnInput) => {
    const text = composerTextOf(
      input,
      clarificationsRef.current,
      messagesRef.current,
    );
    if (text) setInput((current) => (current.trim() ? current : text));
  }, []);

  /**
   * A turn that ended without committing (plan S4.4). One road for the live
   * send, a reload that re-attached to the turn, and a failed turn read back
   * on load. Nothing was saved, so the record it named is open again and its
   * card answerable.
   * - `unsent`: stopped. Not a failure: its bubble goes, and
   *   the user's typed words go back to the composer.
   * - `failed`: its reviewed copy is shown under the turn's bubble, which
   *   stays. When the failing owner allows a Retry, Retry resends its input
   *   until the draft moves on (below); else the words also go back to the
   *   composer now, to be reworded. `announce` (the toast and the log) is
   *   false only for a failure that ended before this page loaded.
   * Resolves the failure's code (empty when it has none, or was not one).
   */
  const endTurnUncommitted = useCallback(
    (
      ended: SessionTurn,
      end:
        | { kind: "unsent" }
        | { kind: "failed"; error: unknown; announce: boolean },
      targetDraftId: string,
    ): string => {
      if (end.kind === "unsent") {
        setTurn(null);
        handBackWords(ended.input);
        return "";
      }
      const failure = formatTurnFailureCopy(end.error, targetDraftId);
      if (end.announce) {
        console.error("Failed to send questionnaire message:", end.error);
        if (failure.code) {
          console.error("Questionnaire turn failure", {
            code: failure.code,
            // The surface code alone cannot be acted on: one code covers a
            // rejected authored question, an unhandled upstream error and a
            // dropped socket. These two say which, and the response body has
            // carried them all along.
            failureCodes: failure.failureCodes,
            retryable: failure.retryable,
            draftId: targetDraftId,
          });
          track(AnalyticsEvents.QUESTIONNAIRE_TURN_FAILURE, {
            code: failure.code,
            retryable: failure.retryable,
          });
        }
        toast.error(failure.title, {
          description: failure.description ?? undefined,
          ...signInActionFor(end.error),
        });
      }
      keepEndedTurn({
        ...ended,
        sendUnconfirmed: false,
        failure: {
          title: failure.title,
          description: failure.description,
          retryable: failure.retryable,
          sameTurn: failure.outcomeUnknown,
          bubble: turnFailureNote(failure.title),
        },
      });
      if (!failure.retryable) handBackWords(ended.input);
      // The record the turn named was answered or replaced elsewhere, or the
      // draft moved past the revision the turn was sent against (a second
      // tab): read the draft as it is now, so the card shows how it was
      // answered and the next send carries the current revision.
      if (DRAFT_MOVED_TURN_REFUSALS.has(failure.code)) {
        void getQuestionnaireDraftApi(targetDraftId).then(
          hydrateFromDraftResponse,
          (reloadError) =>
            console.warn("Failed to reload the chat:", reloadError),
        );
      }
      return failure.code;
    },
    [handBackWords, hydrateFromDraftResponse, keepEndedTurn, setTurn],
  );

  // A failed turn ends once the draft moves past the revision it was sent
  // against (a Save, Build manually, an audience change, a compile): nothing
  // can retry it any more. Words only its Retry still held go back to the
  // composer, so they never vanish with it (B16); a failure without Retry
  // handed them back when it failed. A Retry that has taken the turn
  // (`retryClaimRef`) keeps its words: its own gate's Save moved the draft,
  // and it sends them next.
  const retryClaimRef = useRef<SessionTurn | null>(null);
  useEffect(() => {
    if (!turn?.failure || draftRevision === (turn.revision ?? undefined))
      return;
    setTurn(null);
    if (turn.bubble && turn.failure.retryable && retryClaimRef.current !== turn)
      handBackWords(turn.input);
  }, [draftRevision, handBackWords, setTurn, turn]);

  /**
   * A turn that committed (plan S4.1–S4.3): adopt its transcript, objective
   * and records, its draft with readiness, its compiled preview, and announce
   * what its revision changed on other questions. One road for the live send
   * and a reload that re-attached to the turn. Resolves false when a newer
   * draft was already adopted, or the turn answered with an error.
   */
  const adoptCommittedTurn = useCallback(
    async (
      ended: SessionTurn,
      targetDraftId: string,
      response: Awaited<ReturnType<typeof waitForQuestionnaireChatTurn>>,
      /** Aborted when the page left: nothing more is written or compiled. */
      abortSignal?: AbortSignal,
    ): Promise<boolean> => {
      if (abortSignal?.aborted) return false;
      if (response.draft && !adoptDraftRevision(response.revision))
        return false;
      if (response.draft) {
        setDeclaredAssetTypes(declaredAssetTypesOf(response.draft.testedAssets));
      }
      setChatLanguage(response.chatState.conversationLanguage);
      setMessages((previous) =>
        withLocalNotes(
          previous,
          localizeMessages(response.chatState.messages),
        ),
      );
      // The transcript carries the turn's own message now.
      setTurn({ ...ended, bubble: null });
      setFollowUpCount(response.chatState.assistantFollowUpCount ?? 0);
      setStudyGoals(response.chatState.studyGoals ?? null);
      setClarifications(response.chatState.clarifications ?? []);
      setAssumptions(
        response.chatState.assumptions.map((assumption) => assumption.text),
      );
      setShowSlowWarning(false);

      if (hasWorkspaceDraft(response.draft)) {
        setDraft(response.draft);
        setReadiness(response.readiness ?? null);
        setCompiledSurveyJson(null);
        // Only a draft the backend cleared for compile is sent to the
        // compiler; partial drafts stay in the workspace without a compiled
        // preview.
        if (response.readiness?.canCompile && !abortSignal?.aborted) {
          try {
            const compileResult = await compileDraftSingleFlight(targetDraftId);
            setReadiness(compileResult.readiness ?? response.readiness ?? null);
            // No PATCH back of the compiled JSON: `POST /compile-survey`
            // already persisted these bytes, so re-sending them through the
            // generic draft update is a second write of the same thing.
            setCompiledSurveyJson(compileResult.surveyJson as SurveyJson);
          } catch (error) {
            console.error(
              "Failed to refresh questionnaire draft preview:",
              error,
            );
            toast.error(
              getCompileErrorMessage(
                error,
                "Draft updated, but the preview failed to refresh.",
                {
                  draftId: targetDraftId,
                  resolvedMinimum: response.readiness?.resolvedMinimum,
                },
              ),
            );
          }
        }
      } else {
        // Clarifier / opening-chain turn: refresh testedAssets.
        // Text-collect (claims / pains / pricing / concept) persists assets
        // before PLAN without returning a questionnaire draft; without this
        // GET the confirmation cards never appear.
        try {
          hydrateFromDraftResponse(
            await getQuestionnaireDraftApi(targetDraftId),
          );
        } catch (error) {
          console.warn(
            "Failed to refresh tested assets after chat turn:",
            error,
          );
        }
      }
      return true;
    },
    [compileDraftSingleFlight, hydrateFromDraftResponse, setTurn],
  );

  /**
   * One turn, from its start to its end (plan S4.1–S4.4): the one road for
   * every turn the builder sends, and for a queued or running turn a reload
   * finds (`attach`). While it runs the builder shows its bubble and locks;
   * its end settles through `adoptCommittedTurn` or `endTurnUncommitted`.
   * Resolves whether it committed; false, with nothing started, while another
   * turn is in flight.
   */
  const runTurn = useCallback(
    async (args: {
      draftId: string;
      input: QuestionnaireTurnInput;
      /** A turn already enqueued, found by a reload: its id and row facts. */
      attach?: {
        turnId: string;
        stage: string | null;
        draftRevisionBefore: number | null;
        createdAt: string;
      };
      /**
       * The send this turn is: a Retry of a send whose enqueue got no answer
       * passes its id, so the server answers with the turn it may have
       * queued. Absent for a new send, which mints one.
       */
      clientTurnId?: string;
      /** Aborted when the page leaves this draft; else when it unmounts. */
      abortSignal?: AbortSignal;
    }): Promise<boolean> => {
      const current = turnRef.current;
      if (current && current.failure === null) return false;
      turnSequenceRef.current += 1;
      const { attach, input } = args;
      const pageSignal = args.abortSignal ?? pageAbort.signal;
      // This send's own signal: Stop before the server names the turn ends
      // the wait (`cancelRequest`), and the page leaving ends it too.
      const sendAbort = new AbortController();
      const endSend = () => sendAbort.abort();
      if (pageSignal.aborted) endSend();
      pageSignal.addEventListener("abort", endSend);
      sendAbortRef.current = sendAbort;
      if (attach) setActiveTurnId(attach.turnId);
      const bubbleId = attach ? `local_turn_${attach.turnId}` : localMessageId();
      const bubbleCreatedAt = attach?.createdAt ?? new Date().toISOString();
      const bubbleOf = (turnInput: QuestionnaireTurnInput) =>
        turnBubble(turnInput, bubbleId, bubbleCreatedAt, "clarifying_answer");
      let running: SessionTurn = {
        input,
        clientTurnId: attach ? null : (args.clientTurnId ?? newClientTurnId()),
        bubble: bubbleOf(input),
        revision: attach
          ? attach.draftRevisionBefore
          : (draftRevisionRef.current ?? null),
        startedAt: attach?.createdAt ?? null,
        sendUnconfirmed: false,
        failure: null,
      };
      setTurn(running);
      setSessionError(null);
      assignTurnStage(attach ? (attach.stage ?? "routing") : null);
      try {
        const onStage = (event: { stage: string; total?: number | null }) =>
          assignTurnStage(event.stage, event.total);
        const response = attach
          ? await waitForQuestionnaireChatTurn({
              draftId: args.draftId,
              turnId: attach.turnId,
              abortSignal: pageSignal,
              onStage,
            })
          : await enqueueAndAwaitQuestionnaireChatTurn({
              draftId: args.draftId,
              clientTurnId: running.clientTurnId!,
              // The revision the user sees: the turn acts on it or is refused.
              expectedRevision: revisionToSend(running.revision),
              abortSignal: sendAbort.signal,
              ...(input.command
                ? { command: input.command }
                : { message: input.message ?? "" }),
              // The server resolves the language the turn writes in (a
              // translation's target, else the stamped draft), and the
              // builder's follows the committed draft, never this request.
              ...(input.optionId ? { optionId: input.optionId } : {}),
              ...(input.answersClarificationId
                ? { answers: input.answersClarificationId }
                : {}),
              ...(input.attachments?.length
                ? { attachments: input.attachments }
                : {}),
              onStage,
              onTurnId: setActiveTurnId,
              // The input as the turn's row stores it: an objective edit's
              // bubble shows the change the server recorded, from the start.
              onInput: (stored) => {
                if (turnRef.current !== running) return;
                running = {
                  ...running,
                  input: stored,
                  bubble: bubbleOf(stored),
                };
                setTurn(running);
              },
              // No answer yet: the status line says it is still sending.
              onSendUnconfirmed: (sendUnconfirmed) => {
                if (turnRef.current !== running) return;
                running = { ...running, sendUnconfirmed };
                setTurn(running);
              },
            });
        assignTurnStage(null);
        const committed = await adoptCommittedTurn(
          running,
          args.draftId,
          response,
          pageSignal,
        );
        if (committed && response.stoppedOn) {
          const stopped = stoppedAfterSaveTurn(
            draftRevisionRef.current ?? null,
            response.stoppedOn,
            args.draftId,
          );
          toast.error(stopped.failure!.title, {
            description: stopped.failure!.description ?? undefined,
          });
          keepEndedTurn(stopped);
        } else {
          setTurn(null);
        }
        return committed;
      } catch (error) {
        // The page left this draft: nothing here is shown any more.
        if (pageSignal.aborted) {
          if (turnRef.current?.input === running.input) setTurn(null);
          assignTurnStage(null);
          return false;
        }
        assignTurnStage(null);
        // A Stop (before the turn was named, or accepted by the server) is
        // `unsent`: the words go back to the composer.
        const code = endTurnUncommitted(
          running,
          isTurnCancelled(error)
            ? { kind: "unsent" }
            : { kind: "failed", error, announce: true },
          args.draftId,
        );
        if (code === TURN_IN_FLIGHT) {
          void attachLatestTurnRef.current(args.draftId, pageSignal);
        }
        return false;
      } finally {
        pageSignal.removeEventListener("abort", endSend);
        if (sendAbortRef.current === sendAbort) sendAbortRef.current = null;
      }
    },
    [
      adoptCommittedTurn,
      assignTurnStage,
      endTurnUncommitted,
      keepEndedTurn,
      pageAbort,
      setTurn,
    ],
  );
  const runTurnRef = useRef(runTurn);
  runTurnRef.current = runTurn;
  const endTurnUncommittedRef = useRef(endTurnUncommitted);
  endTurnUncommittedRef.current = endTurnUncommitted;

  // A reload finds the draft's latest turn on its row (plan S4.4). A queued
  // or running one is re-attached and settles through `runTurn`, the same
  // road as a send, so its bubble shows and its end adopts the draft with
  // readiness and compile. A failed one is shown with its Retry only while
  // it is still the latest thing that happened to the draft: the draft is
  // still at the revision the turn was sent against (a failed turn commits
  // nothing, so any later Save, Build manually or audience write moved it).
  // Keyed on the draft alone, so a language change never re-runs it.
  const attachLatestTurn = useCallback(
    async (targetDraftId: string, signal: AbortSignal): Promise<void> => {
      const sequence = turnSequenceRef.current;
      // A turn this session started since the readback went out owns the
      // chat.
      const superseded = () =>
        signal.aborted || turnSequenceRef.current !== sequence;
      // A failed read is retried until it answers: a page that gave up
      // would show no turn while the server holds the draft's lock.
      let readback: QuestionnaireChatTurnReadback | null = null;
      try {
        readback = await readUntilAnswered(
          () => getActiveQuestionnaireChatTurnApi(targetDraftId),
          signal,
        );
      } catch {
        // The page left, or the read was refused (signed out, no access,
        // gone).
        return;
      }
      const snapshot = readback?.turn;
      if (!snapshot || superseded()) {
        return;
      }
      if (snapshot.status === "queued" || snapshot.status === "running") {
        await draftLoadedRef.current?.promise;
        if (superseded() || notEditableReasonRef.current) {
          return;
        }
        await runTurnRef.current({
          draftId: targetDraftId,
          input: snapshot.input,
          attach: {
            turnId: snapshot.turnId,
            stage: snapshot.stage,
            draftRevisionBefore: snapshot.draftRevisionBefore,
            createdAt: snapshot.createdAt,
          },
          abortSignal: signal,
        });
        return;
      }
      if (snapshot.status === "completed" && snapshot.errorCode) {
        // Stopped after a commit (#3): its notice, while that commit is
        // still the draft's latest revision.
        await draftLoadedRef.current?.promise;
        if (
          !superseded() &&
          snapshot.draftRevisionAfter !== null &&
          snapshot.draftRevisionAfter === draftRevisionRef.current
        ) {
          keepEndedTurn(
            stoppedAfterSaveTurn(
              snapshot.draftRevisionAfter,
              snapshot,
              targetDraftId,
            ),
          );
        }
        return;
      }
      if (snapshot.status !== "failed") {
        return;
      }
      // Compared with the draft this page loaded, whichever lands first.
      await draftLoadedRef.current?.promise;
      if (
        superseded() ||
        snapshot.draftRevisionBefore === null ||
        snapshot.draftRevisionBefore !== draftRevisionRef.current
      ) {
        return;
      }
      endTurnUncommittedRef.current(
        {
          input: snapshot.input,
          clientTurnId: null,
          bubble: turnBubble(
            snapshot.input,
            `local_turn_${snapshot.turnId}`,
            snapshot.createdAt,
            "clarifying_answer",
          ),
          revision: snapshot.draftRevisionBefore,
          startedAt: snapshot.createdAt,
          sendUnconfirmed: false,
          failure: null,
        },
        {
          kind: "failed",
          // The owner's persisted code and verdict, in the shape a live
          // failure has.
          error: turnEndError(snapshot),
          announce: false,
        },
        targetDraftId,
      );
    },
    [keepEndedTurn],
  );
  attachLatestTurnRef.current = attachLatestTurn;
  useEffect(() => {
    if (!draftId) {
      return;
    }
    const abortController = new AbortController();
    void attachLatestTurn(draftId, abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [attachLatestTurn, draftId]);

  useEffect(() => {
    if (bootstrapRef.current) {
      return;
    }
    bootstrapRef.current = true;

    const bootstrap = async (): Promise<string | null> => {
      // The page's first message (the wizard's brief, or the re-plan Review
      // sends back) is an ordinary turn: the same send, bubble, failure,
      // Retry and hand-back as a message typed in the composer.
      async function sendBootInitialMessage(targetDraftId: string) {
        const text = initialMessage?.trim();
        if (!text) {
          return;
        }
        // A turn a reload re-attached to owns the chat: the words wait in
        // the composer rather than being dropped.
        if (turnRef.current && turnRef.current.failure === null) {
          setInput(text);
          return;
        }
        await sendTurnRef.current?.({ text, draftId: targetDraftId });
      }

      try {
        if (resumeDraftId) {
          const restoredDraft = await getQuestionnaireDraftApi(resumeDraftId);

          // When the stored audience is still the placeholder (i.e. a completed
          // duplicate whose audience was just chosen in the wizard), persist the
          // selected population before hydrating so the backend LLM context
          // reflects the real audience, not the placeholder.
          const hasPlaceholderAudience = draftNamesNoPopulation(
            restoredDraft.artifacts?.draft?.audience,
          );

          if (selectedPopulation && hasPlaceholderAudience) {
            // Written at the revision just read: a planned draft refuses a
            // save that names none (manual_edit_revision_required).
            const updated = await persistDraftPatch(
              resumeDraftId,
              {
                audience: buildDraftAudience(selectedPopulation),
              },
              restoredDraft.revision,
            );
            hydrateFromDraftResponse(updated);
            // Only a draft the backend says can compile is sent to the
            // compiler. A duplicated partial WIP draft (e.g. a manual-start
            // draft with one question) stays in the workspace without a
            // compiled preview — compiling it would 400.
            if (updated.readiness?.canCompile) {
              try {
                const compileResult =
                  await compileDraftSingleFlight(resumeDraftId);
                setCompiledSurveyJson(compileResult.surveyJson as SurveyJson);
                setReadiness(
                  compileResult.readiness ?? updated.readiness ?? null,
                );
              } catch (compileError) {
                console.error(
                  "Failed to compile duplicated draft after audience selection:",
                  compileError,
                );
                toast.error(
                  getCompileErrorMessage(
                    compileError,
                    "Audience saved, but the preview failed to compile.",
                    {
                      draftId: resumeDraftId,
                      resolvedMinimum: updated.readiness?.resolvedMinimum,
                    },
                  ),
                );
              }
            }
          } else {
            // A compile-ready draft whose persisted compiled survey JSON is
            // missing (never persisted, or cleared by a prior turn) would
            // otherwise leave "Review study" disabled forever — the button
            // is gated on the compiled JSON, not the question count. Recompile
            // (and re-persist) it and hydrate with the result in the SAME pass:
            // the compiled preview must be present when the draft is first
            // adopted, because the workspace only syncs its survey JSON when the
            // draft itself changes — a value set after hydration never reaches
            // the button.
            let resumeResponse = restoredDraft;
            if (
              !restoredDraft.artifacts.compiledSurveyJson &&
              restoredDraft.readiness?.canCompile
            ) {
              try {
                const compileResult =
                  await compileDraftSingleFlight(resumeDraftId);
                // The compile committed the survey into the bundle at a new
                // revision, which the single flight has adopted. The load is
                // hydrated at that revision, with the compile's draft; at the
                // GET's older one the never-walk-back guard refused the whole
                // hydration, and the page showed the draft with no chat and
                // no readiness until the next reload.
                resumeResponse = {
                  ...restoredDraft,
                  revision: compileResult.revision ?? restoredDraft.revision,
                  artifacts: {
                    ...restoredDraft.artifacts,
                    ...(compileResult.draft
                      ? { draft: compileResult.draft }
                      : {}),
                    compiledSurveyJson: compileResult.surveyJson,
                  },
                  readiness: compileResult.readiness ?? restoredDraft.readiness,
                };
              } catch (compileError) {
                console.error(
                  "Failed to recompile resumed draft preview:",
                  compileError,
                );
                // Surface the failure instead of silently leaving the button
                // disabled behind the generic question-count tooltip —
                // consistent with the duplicated-draft and create+chat branches.
                toast.error(
                  getCompileErrorMessage(
                    compileError,
                    "Couldn't prepare this questionnaire for review.",
                    {
                      draftId: resumeDraftId,
                      resolvedMinimum: restoredDraft.readiness?.resolvedMinimum,
                    },
                  ),
                );
              }
            }
            hydrateFromDraftResponse(resumeResponse);
          }
          markDraftLoaded();
          await sendBootInitialMessage(resumeDraftId);
          return resumeDraftId;
        }

        const createdDraft = await createQuestionnaireDraftApi({
          // `{}` while no audience is chosen: the draft's "none yet".
          audience: selectedPopulation
            ? buildDraftAudience(selectedPopulation)
            : {},
          mode: "custom",
          // The questionnaire's language: stamped on the draft, read by every
          // turn after it.
          language,
          // The chat starts in the builder's interface language; the reply's
          // reported language moves it after each turn (wave G, D).
          conversationLanguage: interfaceLanguageCode(),
          // Goal-first seeding (§0): the backend arms the authored opening
          // chain off goalId; intake is always empty (composer-as-forms).
          ...(goalId ? { goalId } : {}),
          ...(sourceThreadToken ? { sourceThreadToken } : {}),
        });

        hydrateFromDraftResponse(createdDraft);
        markDraftLoaded();
        revalidateRail();

        await sendBootInitialMessage(createdDraft.id);
        return createdDraft.id;
      } catch (error) {
        console.error("Failed to bootstrap questionnaire draft:", error);
        // The catalog's line for a coded refusal, the transport line, else
        // the start's own line; Retry only when the outcome is unknown.
        const line = requestFailureLine(
          error,
          i18n.t("questionnaire.bootstrapFailed", {
            ns: "app",
            lng: chatLanguage(),
          }),
          chatLanguage(),
        );
        setMessages([buildFallbackQuestion()]);
        setSessionError(line);
        setBootstrapFailure({
          retryable: transportFailureCopy(error)?.retryable === true,
        });
        appendTurnFailureBubble(setMessages, line);
        toast.error(line);
        return null;
      } finally {
        markDraftLoaded();
      }
    };

    bootstrapRunRef.current = bootstrap;
    void bootstrap();
  }, [
    compileDraftSingleFlight,
    hydrateFromDraftResponse,
    initialMessage,
    markDraftLoaded,
    persistDraftPatch,
    resumeDraftId,
    selectedPopulation,
    setBootstrapFailure,
    sourceThreadToken,
  ]);

  // Wave 8: the pre-draft panel's write path for `StudyObjectivesRow`, before
  // any questionnaire exists. Narrow endpoint on purpose (see the plan) so a
  // save here never clobbers a chat transcript the server has since appended
  // to. `source` is coerced to the endpoint's narrower contract: 'confirmed'
  // only when the caller says so, 'edited' otherwise (never 'inferred', which
  // a raw PATCH has no business asserting).
  const saveStudyGoals = useCallback(
    async (next: StudyObjectiveUpdate): Promise<boolean> => {
      if (!draftId) {
        throw new Error("Draft session is not ready");
      }

      const previousStudyGoals = studyGoals;
      const source: StudyGoals["source"] =
        next.source === "confirmed" ? "confirmed" : "edited";

      // Optimistic: the row already committed this value to its own state one
      // render earlier (it calls us from a deferred save), so mirroring it
      // here immediately avoids a round-trip flicker while the PATCH is in
      // flight. Spreads the prior value first so `framing`, `stated_versions`,
      // and `stated_segments` survive a decision/learn-only edit.
      setStudyGoals({
        ...(previousStudyGoals ?? { learn: [] }),
        decision: next.decision,
        learn: next.learn ?? previousStudyGoals?.learn ?? [],
        source,
        // Same rule as the workspace's own edit path: the coded-fallback stamp
        // describes a sentence, so the spread must not carry it onto a
        // different one. The PATCH response replaces this with the server's
        // own derivation a moment later.
        ...carriedObjectiveFallbackStamp(previousStudyGoals, next.decision),
      });
      setIsSavingStudyGoals(true);

      try {
        // A turn (plan S4.2, L23): the chat's lock, stages and cancel, and a
        // question its restructure asks is stored and answered in the chat.
        // The settled turn replaces the optimistic value with the server's.
        // One that did not commit reverts it, and the result goes back to
        // the row, which reopens its editor on the text.
        const committed =
          (await sendTurnRef.current?.({
            command: {
              kind: "set_objective",
              studyGoals: {
                decision: next.decision,
                ...(next.learn !== undefined ? { learn: next.learn } : {}),
                source,
              },
            },
          })) ?? false;
        if (!committed) setStudyGoals(previousStudyGoals);
        return committed;
      } finally {
        setIsSavingStudyGoals(false);
      }
    },
    [draftId, studyGoals],
  );

  const persistDraftEdits = useCallback(
    async (
      baseDraft: QuestionnaireDraft,
      nextDraft: QuestionnaireDraft,
      expectedRevision?: number,
    ): Promise<SavedDraftResult> => {
      if (!draftId) {
        throw new Error("Draft session is not ready");
      }
      const operations = diffDraftToOperations(baseDraft, nextDraft);
      if (operations.length === 0) {
        // Only server-derived fields differ: the accepted draft already says
        // everything these edits say.
        return {
          draft: baseDraft,
          surveyJson: compiledSurveyJson,
          readiness,
          revision: expectedRevision ?? draftRevisionRef.current,
          effects: [],
        };
      }
      return persistOperations(
        operations,
        (latest) => diffDraftToOperations(latest, nextDraft).length === 0,
        expectedRevision,
      );
    },
    [compiledSurveyJson, draftId, persistOperations, readiness],
  );

  const validateDraftEdits = useCallback(
    async (
      baseDraft: QuestionnaireDraft,
      nextDraft: QuestionnaireDraft,
      expectedRevision?: number,
    ): Promise<QuestionnaireValidateResponse | null> => {
      const basis = expectedRevision ?? draftRevisionRef.current;
      if (!draftId || basis === undefined) return null;
      const operations = diffDraftToOperations(baseDraft, nextDraft);
      if (operations.length === 0) return null;
      return validateQuestionnaireOperationsApi(draftId, {
        expectedRevision: basis,
        operations,
      });
    },
    [draftId],
  );

  const compilePersistedDraft = useCallback(async () => {
    if (!draftId) {
      return { surveyJson: null, readiness: null, revision: null, audience: null };
    }

    try {
      const compileResult = await compileDraftSingleFlight(draftId);
      const surveyJson = compileResult.surveyJson as SurveyJson;
      const compiledReadiness = compileResult.readiness ?? null;
      setCompiledSurveyJson(surveyJson);
      if (compiledReadiness) {
        setReadiness(compiledReadiness);
      }
      return {
        surveyJson,
        readiness: compiledReadiness,
        revision: compileResult.revision ?? null,
        audience: compileResult.draft?.audience ?? null,
      };
    } catch (error) {
      console.error("Failed to compile the persisted draft:", error);
      toast.error(
        getCompileErrorMessage(
          error,
          "Couldn't prepare this questionnaire for review. Try again.",
          {
            draftId,
            resolvedMinimum: readiness?.resolvedMinimum,
          },
        ),
      );
      return { surveyJson: null, readiness: null, revision: null, audience: null };
    }
  }, [compileDraftSingleFlight, draftId, readiness?.resolvedMinimum]);

  const startManualDraft = useCallback(
    async (options?: {
      intent?: "manual" | "paste";
      answers?: { clarificationId: string; optionId?: string };
    }) => {
      if (!draftId) {
        throw new Error("Draft session is not ready");
      }
      const previousDraft = draft;
      // The opened workspace is a fact of the stored draft: its `status`,
      // which the normalizer keeps only when the write names it (an intake
      // shell stays intake otherwise). A reload opens the workspace from it,
      // and the next chat turn revises the opened workspace instead of
      // building (`hasWorkspaceDraft`, Nest's `isOpenedQuestionnaireWorkspace`).
      // The manual road's first stamp (C12-2), as a Build stamps its draft:
      // a stamped draft keeps its language, an unstamped one takes the pick.
      const seededDraft = {
        ...((draft as Record<string, unknown> | null) ?? {}),
        researchQuestions: [],
        studyGoals,
        status: "draft_ready",
        contentLanguage: draft?.contentLanguage ?? language,
        // With no stored draft this is a seed, not a complete draft: the
        // server normalizes it on write.
      } as unknown as QuestionnaireDraft;

      const intent = options?.intent === "paste" ? "paste" : "manual";
      const ackPrefix = intent === "paste" ? "paste-start-" : "manual-start-";
      const ackContent = chatT(chatLanguage())(
        intent === "paste"
          ? "questionnaire.manualStart.paste"
          : "questionnaire.manualStart.manual",
      );
      const ackMessage: QuestionnaireChatMessage = {
        id: `${ackPrefix}${Date.now()}`,
        role: "assistant",
        language: chatLanguage(),
        content: ackContent,
        kind: "system_status",
        createdAt: new Date().toISOString(),
      };

      // Open the workspace on this click. Waiting for persist left the empty
      // placeholder sitting on a half-ready panel for a second.
      setDraft(seededDraft);
      setCompiledSurveyJson(null);
      setMessages((current) =>
        current.some((message) =>
          typeof message?.id === "string"
            ? message.id.startsWith(ackPrefix)
            : false,
        )
          ? current
          : [...current, ackMessage],
      );

      try {
        const savedDraftResponse = await persistDraftPatch(draftId, {
          // The server opens the workspace (T1a: a typed study fact through
          // the one commit); the seed above is this tab's view until then.
          openWorkspace: {
            contentLanguage: draft?.contentLanguage ?? language,
          },
          // The card the workspace opens from is answered in this write.
          ...(options?.answers ? { answers: options.answers } : {}),
        });
        setClarifications(
          parseChatTranscript(savedDraftResponse.artifacts.chatTranscript)
            .clarifications,
        );
        const savedDraft =
          syncDraftAssetsFromResponse(savedDraftResponse) ??
          (savedDraftResponse.artifacts.draft as QuestionnaireDraft);

        // This acknowledgement is local UI feedback. The accepted transcript
        // belongs to the chat turn owner; manual save must not patch it again.

        setDraft(savedDraft);
        setReadiness(savedDraftResponse.readiness ?? null);
        return savedDraft;
      } catch (error) {
        // Take back only this click's optimistic seed. A 409 has already
        // adopted the draft the server holds (and its revision); that, or any
        // draft accepted meanwhile, stays. The pre-click draft must never
        // come back under a newer revision.
        setDraft((current) =>
          current === seededDraft ? previousDraft : current,
        );
        setMessages((current) =>
          current.filter((message) => message.id !== ackMessage.id),
        );
        console.error("Failed to start manual draft:", error);
        toast.error(
          getCompileErrorMessage(
            error,
            "Failed to start a manual questionnaire.",
          ),
        );
        throw error;
      }
    },
    [draft, draftId, language, persistDraftPatch, studyGoals],
  );

  /**
   * One chat turn the user sends: their words, or a typed command (plan
   * S4.2). It names the open clarification it answers, by id (plan S4.1); the
   * records stay the server's, and the builder shows the answer from the turn
   * in flight. Resolves whether the turn committed; false when it failed, was
   * stopped, or could not start, so a caller's optimistic state can revert.
   */
  /** Run the page's start again after it failed (B17). */
  const restartBootstrap = useCallback(async (): Promise<string | null> => {
    const run = bootstrapRunRef.current;
    if (!run) return null;
    setBootstrapFailure(null);
    setSessionError(null);
    return run();
  }, [setBootstrapFailure]);

  const sendTurn = useCallback(
    async (args: {
      text?: string;
      command?: QuestionnaireTurnCommand;
      optionId?: string;
      /** The words are the composer's (see `sendMessage`). */
      from?: "composer";
      /** The stored files the words carry (L8). */
      attachments?: Array<QuestionnaireTurnAttachmentInput>;
      /** The draft the page just created or loaded, before state has it. */
      draftId?: string;
    }): Promise<boolean> => {
      const targetDraftId = args.draftId ?? draftId;
      const text = args.text?.trim() ?? "";
      if (!args.command && !text) return false;
      if (!targetDraftId) {
        // The page's start failed, so there is no draft yet: start it
        // again, then send to the draft it made (B17).
        const started = bootstrapFailureRef.current
          ? await restartBootstrap()
          : null;
        return started
          ? (sendTurnRef.current?.({ ...args, draftId: started }) ?? false)
          : false;
      }
      // Typed words answer the open card, whatever their shape: a numbered
      // paste on a collect card is its material. Only the confirm card's
      // "Paste questionnaire" and "Build manually" options open a workspace,
      // chosen by id (`startManualDraft`). The turn reads the persisted draft
      // (plan S4.2).
      const input: QuestionnaireTurnInput = {
        message: args.command ? null : text,
        command: args.command ?? null,
        optionId: args.optionId ?? null,
        answersClarificationId:
          openClarificationOf({ clarifications: clarificationsRef.current })
            ?.id ?? null,
        ...(!args.command && args.attachments?.length
          ? { attachments: args.attachments }
          : {}),
      };
      const sent = runTurn({ draftId: targetDraftId, input });
      // The composer's words leave it with their turn, and a turn that does
      // not commit hands them back. Any other turn leaves them unsent.
      if (args.from === "composer" && turnRef.current?.input === input) {
        setInput("");
      }
      return sent;
    },
    [draftId, restartBootstrap, runTurn],
  );
  sendTurnRef.current = sendTurn;

  const sendMessage = useCallback(
    (
      text: string,
      optionId?: string,
      from?: "composer",
      attachments?: Array<QuestionnaireTurnAttachmentInput>,
    ) => sendTurn({ text, optionId, from, attachments }),
    [sendTurn],
  );

  const sendCommand = useCallback(
    (command: QuestionnaireTurnCommand) => sendTurn({ command }),
    [sendTurn],
  );

  const answerWithNextAudienceChange = useCallback(
    (answers: { clarificationId: string; optionId?: string }) => {
      audienceAnswerRef.current = answers;
    },
    [],
  );

  // Retry resends the failed turn's own input, as its row stores it (plan
  // S4.1, S4.2): the words or the command, the card option they chose, and
  // the record they answer. Live and after a reload alike. A turn that
  // stopped after its commit resumes instead (`RESUME_TURN_INPUT`).
  //
  // `beforeSend` is a gate the Retry passes first (the builder's "Save and
  // continue" over unsaved edits). The Retry takes the failed turn before the
  // gate runs: its words, command, card option, record and send id. A gate
  // whose Save moves the draft ends the failed slot, but not the Retry: it
  // sends what it took once the gate passes. A gate that declines sends
  // nothing and leaves the failed turn as it was. Once it sends, the attempt
  // it retries leaves the transcript: the new attempt is the same turn.
  const retryLast = useCallback(
    (beforeSend?: () => Promise<boolean>) => {
      // A failed start has no turn: its Retry runs the start again (B17).
      if (bootstrapFailureRef.current?.retryable) {
        void restartBootstrap();
        return;
      }
      const failed = turnRef.current;
      if (!failed?.failure || !draftId) {
        return;
      }
      const resend = {
        draftId,
        input: failed.input,
        ...(failed.failure.sameTurn && failed.clientTurnId
          ? { clientTurnId: failed.clientTurnId }
          : {}),
      };
      retryClaimRef.current = failed;
      const attempt = new Set(
        [failed.bubble?.id, failed.failure.bubble.id].filter(Boolean),
      );
      void (async () => {
        try {
          if (beforeSend && !(await beforeSend())) return;
          setMessages((current) =>
            current.filter((message) => !attempt.has(message.id)),
          );
          await runTurn(resend);
        } finally {
          if (retryClaimRef.current === failed) retryClaimRef.current = null;
        }
      })();
    },
    [draftId, restartBootstrap, runTurn],
  );

  // Stop is a request (plan S4.4, L25). The turn keeps its lock and the waiter
  // keeps listening: the terminal event says whether it stopped (`cancelled`,
  // nothing saved) or its commit had already landed (`completed`), in which
  // case the draft is adopted like any finished turn. It acts on the current
  // turn's id only, once: a request that was not accepted offers Stop again.
  // Before the server names the turn, Stop ends this page's send only: its
  // words go back to the composer, and a turn that did land is found by the
  // reload's road, or by the next send's `questionnaire_turn_in_flight`.
  const canStop =
    activeTurnId !== null
      ? stopRequestedTurnId !== activeTurnId
      : turn !== null && turn.failure === null && turn.clientTurnId !== null;
  const cancelRequest = useCallback(() => {
    const turnId = activeTurnId;
    const id = draftId;
    if (!turnId) {
      sendAbortRef.current?.abort();
      return;
    }
    if (!id || stopRequestedTurnId === turnId) {
      return;
    }
    setStopRequestedTurnId(turnId);
    setShowSlowWarning(false);
    void cancelQuestionnaireChatTurnApi(id, turnId).catch(() => {
      setStopRequestedTurnId((current) => (current === turnId ? null : current));
    });
  }, [activeTurnId, draftId, stopRequestedTurnId]);

  const isDraftReady = useMemo(
    () => Boolean(compiledSurveyJson),
    [compiledSurveyJson],
  );

  const appendAssistantPrompt = useCallback(
    (args: {
      content: string;
      options?: Array<{ label: string; value: string }>;
    }) => {
      const id = localMessageId();
      setMessages((current) => [
        ...current,
        {
          id,
          role: "assistant",
          // A line the builder composes itself is in the language the chat
          // speaks, so the card that shows it words its own chrome (and reads
          // left to right or right to left) from that, never the interface's.
          language: chatLanguage(),
          content: args.content,
          kind: "system_status",
          createdAt: new Date().toISOString(),
          ...(args.options ? { options: args.options } : {}),
        },
      ]);
      return id;
    },
    [],
  );

  // A failed turn is shown only while the draft is at the revision the turn
  // was sent against (plan S4.4); the effect above ends it after a later
  // write, and this keeps the render before that effect from showing it.
  const shownFailure =
    turn?.failure && draftRevision === (turn.revision ?? undefined)
      ? turn.failure
      : null;
  const inFlightTurn = useMemo<InFlightTurn | null>(
    () =>
      turn && turn.failure === null
        ? { input: turn.input, messageId: turn.bubble?.id ?? null }
        : null,
    [turn],
  );
  // The transcript as it was before the running turn, then the turn's own
  // bubble: what a found turn already committed (`startedAt`) waits for its
  // written reply. A turn that ended short is in the transcript already
  // (`keepEndedTurn`).
  const shownMessages = useMemo(() => {
    if (!turn?.bubble || turn.failure !== null) return messages;
    const startedAt = turn.startedAt === null ? null : Date.parse(turn.startedAt);
    const before =
      startedAt === null
        ? messages
        : messages.filter(
            (message) =>
              message.id.startsWith("local_") || Date.parse(message.createdAt) < startedAt,
          );
    return [...before, turn.bubble];
  }, [messages, turn]);

  return {
    appendAssistantPrompt,
    persistOperations,
    presentation,
    draftRevision,
    assumptions,
    cancelRequest,
    canStop,
    compiledSurveyJson,
    compilePersistedDraft,
    draft,
    draftId,
    followUpCount,
    turnStage,
    turnStageTotal,
    stopRequested:
      sending &&
      activeTurnId !== null &&
      stopRequestedTurnId === activeTurnId,
    input,
    isDraftReady,
    isSavingStudyGoals,
    loadingDraft,
    messages: shownMessages,
    persistDraftEdits,
    validateDraftEdits,
    readiness,
    canRetryLast:
      shownFailure?.retryable === true || bootstrapFailure?.retryable === true,
    notEditableReason,
    retryLast,
    saveStudyGoals,
    sendMessage,
    sendCommand,
    sending,
    sendUnconfirmed: sending && turn.sendUnconfirmed,
    sessionError: shownFailure
      ? shownFailure.description
        ? `${shownFailure.title} ${shownFailure.description}`
        : shownFailure.title
      : sessionError,
    setInput,
    showSlowWarning,
    startManualDraft,
    studyGoals,
    clarifications,
    declaredAssetTypes,
    inFlightTurn,
    answerWithNextAudienceChange,
  };
}
